/* eslint-disable unicorn/prefer-query-selector */
/* eslint-disable unicorn/prefer-dom-node-dataset */
/* eslint-disable unicorn/prefer-dom-node-append */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import * as Sentry from '@sentry/react';
import { useOpenTutorWithDelayedAction } from 'src/features/room/components/tutor/hooks';
import { AskEchoButton } from './echo.styles';
import { useTextSelectionToAskEcho } from './hooks/use-text-selection-to-ask-echo';
interface AskEchoSelectableTextProps {
  children: React.ReactNode;
  taskId: string;
  roomCode: string;
}
export const ECHO_IDS = {
  CONTAINER: 'ask-echo-selectable-text-container',
  BUTTON_CONTAINER: 'ask-echo-button-container',
  BUTTON: 'ask-echo-button'
};
const createButtonContainer = (parent: HTMLElement | null, taskId: string) => {
  if (!parent) return;
  removeButtonContainer(taskId);
  const container = document.createElement('div');
  container.id = `${ECHO_IDS.BUTTON_CONTAINER}-${taskId}`;
  container.setAttribute('data-testid', ECHO_IDS.BUTTON_CONTAINER);
  parent.append(container);
  return container;
};
const removeButtonContainer = (taskId: string) => {
  const container = document.getElementById(`${ECHO_IDS.BUTTON_CONTAINER}-${taskId}`);
  if (container) {
    container.remove();
  }
};
export const AskEchoSelectableText = ({
  children,
  taskId,
  roomCode
}: AskEchoSelectableTextProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    openTutorWithDelayedAction
  } = useOpenTutorWithDelayedAction(roomCode);
  const {
    selectedText,
    setPopperElement,
    popperStyles,
    popperAttributes,
    onSelectEnd,
    onSelectStart,
    onSelectedTextClick,
    clearSelection
  } = useTextSelectionToAskEcho(ref.current);
  const [buttonContainer, setButtonContainer] = useState<Element | null>(null);
  useEffect(() => {
    const container = createButtonContainer(ref.current, taskId);
    setButtonContainer(container ?? null);
    return () => {
      removeButtonContainer(taskId);
      setButtonContainer(null);
    };
  }, [taskId]);
  useEffect(() => {
    if (!ref.current) return;
    const container = ref.current;

    // Storing the selected text in state
    container.addEventListener('selectstart', onSelectStart);

    // This is necessary to handle the case where the user selects text and then drags the mouse outside the container
    const handleGlobalMouseUp = () => {
      const selection = window.getSelection();
      if (!selection || !ref.current || !selection.rangeCount) return;
      try {
        const range = selection.getRangeAt(0);
        const containerRange = document.createRange();
        containerRange.selectNode(ref.current);

        // Check if both the start and end containers are within our container
        const isStartInContainer = container.contains(range.startContainer);
        const isEndInContainer = container.contains(range.endContainer);
        if (selection.toString() && isStartInContainer && isEndInContainer) {
          onSelectEnd();
        }
      } catch (error) {
        Sentry.captureException(error);
        clearSelection();
      }
    };

    // Hiding the button when clicking in the selected text
    container.addEventListener('mousedown', onSelectedTextClick);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    // Handle clicks outside container
    const handleOutsideClick = (e: MouseEvent) => {
      if (!container.contains((e.target as Node))) {
        clearSelection();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    const handleSelectionChange = () => {
      if (selectedText && selectedText.text) {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || selection.toString() === '') {
          clearSelection();
        }
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      container.removeEventListener('selectstart', onSelectStart);
      container.removeEventListener('mousedown', onSelectedTextClick);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [onSelectEnd, onSelectStart, onSelectedTextClick, clearSelection, selectedText]);
  const buttonElement = selectedText && selectedText.text && <div ref={setPopperElement} style={popperStyles.popper} {...popperAttributes.popper}>
      <AskEchoButton onClick={() => {
      openTutorWithDelayedAction({
        type: 'ask-echo',
        payload: {
          selectedText: selectedText.text,
          taskId,
          surroundingText: selectedText.context
        }
      });
      clearSelection();
    }} data-testid={ECHO_IDS.BUTTON}>
        <FontAwesomeIcon icon={faSparkles} />
        <span>Ask Echo</span>
      </AskEchoButton>
    </div>;
  return <>
      <div ref={ref} data-testid={ECHO_IDS.CONTAINER}>
        {children}
      </div>
      {buttonElement && buttonContainer && createPortal(buttonElement, buttonContainer)}
    </>;
};